import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Users, Watch, GraduationCap } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

const features = [
  {
    icon: Brain,
    title: 'AI-Powered Insights',
    description: 'Gemini 2.5 Flash analyzes your relationships and provides actionable strategies for improvement.',
    color: 'from-purple-500 to-pink-500'
  },
  {
    icon: Users,
    title: 'VR Role-Play',
    description: 'Practice difficult conversations with Tavus AI avatars in immersive virtual environments.',
    color: 'from-blue-500 to-cyan-500'
  },
  {
    icon: Watch,
    title: 'Wearable Integration',
    description: 'Sync with Apple Watch/Fitbit to correlate physiological stress with mood logs and insights.',
    color: 'from-green-500 to-teal-500'
  },
  {
    icon: GraduationCap,
    title: 'Community & Learning',
    description: 'Join a peer forum or take micro-courses to improve communication, empathy, and conflict resolution.',
    color: 'from-orange-500 to-red-500'
  }
];

export const Features: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section id="features" className="py-24 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Transform Your{' '}
            <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
              Relationships
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Discover how AI-powered insights can help you build stronger, more meaningful connections
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              className="group relative"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={prefersReducedMotion ? {} : { y: -10 }}
            >
              <div className="h-full p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 hover:border-white/40 dark:hover:border-gray-600/40 transition-all duration-300">
                {/* Icon */}
                <motion.div
                  className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${feature.color} mb-6`}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <feature.icon className="w-8 h-8 text-white" />
                </motion.div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {feature.description}
                </p>

                {/* Glow Effect */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Feature Showcase */}
        <motion.div
          className="mt-24 grid lg:grid-cols-3 gap-8"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {/* Family Relationships */}
          <div className="group p-8 rounded-3xl bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 backdrop-blur-xl border border-purple-200/30 dark:border-purple-700/30 hover:border-purple-300/50 dark:hover:border-purple-600/50 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Family Focus</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Strengthen family bonds with personalized insights for better communication between parents, children, and siblings.
            </p>
          </div>

          {/* Pet Relationships */}
          <div className="group p-8 rounded-3xl bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 backdrop-blur-xl border border-green-200/30 dark:border-green-700/30 hover:border-green-300/50 dark:hover:border-green-600/50 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <Brain className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Pet Bonding</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Understand your pet's needs better with behavioral analysis and mood tracking for optimal care.
            </p>
          </div>

          {/* Work Relationships */}
          <div className="group p-8 rounded-3xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-xl border border-blue-200/30 dark:border-blue-700/30 hover:border-blue-300/50 dark:hover:border-blue-600/50 transition-all duration-300">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Professional Growth</h3>
            <p className="text-gray-600 dark:text-gray-300">
              Improve workplace relationships with colleagues, managers, and teams through better communication strategies.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};